import { useState } from "react";
import { Button, Select } from "risa-oasis-ui_v2";
import { AuthStatusOptionModel } from "../../../../../../data-model/authStatusOptions";
import { BOOptionModel } from "../../../../../../data-model/boOptionModel";
import { ModalId } from "../../../../../../../shared/enums/modalId";
import AuthOptionsModal from "../authOptionsModal/authOptionsModal";
import DraggableOptionCard from "../draggableOptionCard/draggableOptionCard";
import OptionsDeleteModal from "../optionsDeleteModal/optionsDeleteModal";
import ReorderModal, { SortOption } from "../reorderModal/reorderModal";
interface OrganizationOption {
  label: string;
  value: string;
}

interface AuthOptionsSectionProps {
  sortedOptionsList: AuthStatusOptionModel[];
  optionsList: AuthStatusOptionModel[];
  draggedIndex: number | null;
  sortOption: SortOption;
  showModal: boolean;
  modalMode: "add" | "edit";
  editingOption: AuthStatusOptionModel | null;
  showDeleteModal: boolean;
  deletingOption: AuthStatusOptionModel | null;
  showReorderModal: boolean;
  organizationOptions: OrganizationOption[];
  selectedOrgId: string;
  boOptions: BOOptionModel[];
  onAdd: () => void;
  onEdit: (option: AuthStatusOptionModel) => void;
  onDeleteClick: (option: AuthStatusOptionModel) => void;
  onModalClose: () => void;
  onSave: (option: AuthStatusOptionModel) => Promise<void>;
  onDeleteConfirm: () => Promise<void>;
  onDeleteCancel: () => void;
  onCopyFromOrg: (sourceOrgId: string) => Promise<void>;
  onReorderClick: () => void;
  onReorderModalClose: () => void;
  onReorderSave: (sortOption: SortOption) => void;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent, index: number) => void;
}

const AuthOptionsSection = ({
  sortedOptionsList,
  optionsList,
  draggedIndex,
  sortOption,
  showModal,
  modalMode,
  editingOption,
  showDeleteModal,
  deletingOption,
  showReorderModal,
  organizationOptions,
  selectedOrgId,
  boOptions,
  onAdd,
  onEdit,
  onDeleteClick,
  onModalClose,
  onSave,
  onDeleteConfirm,
  onDeleteCancel,
  onCopyFromOrg,
  onReorderClick,
  onReorderModalClose,
  onReorderSave,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
}: AuthOptionsSectionProps) => {
  const [selectedSourceOrg, setSelectedSourceOrg] = useState<string>("");
  const [isCopying, setIsCopying] = useState(false);

  const filteredOrgOptions = organizationOptions.filter(
    (org) => org.value !== selectedOrgId,
  );

  const handleCopyClick = async () => {
    if (!selectedSourceOrg) return;
    setIsCopying(true);
    try {
      await onCopyFromOrg(selectedSourceOrg);
      setSelectedSourceOrg("");
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <>
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-700">
            Auth Options (Sorted by{" "}
            {sortOption === "priority" ? "Priority" : "Left Pane Display Order"}
            )
          </div>
          <div className="flex items-center gap-2">
            <Button
              disabled={false}
              children={"Reorder"}
              onClick={onReorderClick}
              buttonType={"secondary"}
              size={"small"}
            />
            <Button
              disabled={false}
              children={"Add Auth Option"}
              onClick={onAdd}
              buttonType={"primary"}
              size={"small"}
            />
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-2 overflow-auto">
          {sortedOptionsList.length === 0 ? (
            <div className="flex h-full flex-1 flex-col items-center justify-center gap-4">
              <div className="text-small text-primaryGray-6">
                No auth options found
              </div>
              {filteredOrgOptions.length > 0 && (
                <div className="flex flex-col items-center gap-3 rounded-md border border-primaryGray-15 bg-primaryGray-16 px-5 py-4">
                  <div className="text-tiny text-primaryGray-9">
                    Copy from another organization
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-52">
                      <Select
                        id="copyFromOrgAuth"
                        options={filteredOrgOptions}
                        label=""
                        placeholder="Select Organization"
                        onOptionChange={(option: { value: string }) =>
                          setSelectedSourceOrg(option.value)
                        }
                        defaultValue={selectedSourceOrg}
                      />
                    </div>
                    <Button
                      disabled={!selectedSourceOrg || isCopying}
                      onClick={handleCopyClick}
                      buttonType="primary"
                      size="small"
                    >
                      {isCopying ? "Copying..." : "Copy"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            sortedOptionsList.map((option, index) => (
              <div
                key={option.id}
                draggable
                onDragStart={(e) => onDragStart(e, index)}
                onDragOver={onDragOver}
                onDragEnd={onDragEnd}
                onDrop={(e) => onDrop(e, index)}
              >
                <DraggableOptionCard
                  label={option.text}
                  value={option.id}
                  priority={
                    sortOption === "priority"
                      ? option.priority
                      : option.left_pane_display_order
                  }
                  color={option.bgColor}
                  textColor={option.textColor}
                  isDragging={draggedIndex === index}
                  onEdit={() => onEdit(option)}
                  onDelete={() => onDeleteClick(option)}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {showModal && (
        <AuthOptionsModal
          data={editingOption}
          onModalClose={onModalClose}
          onSave={onSave}
          mode={modalMode}
          existingOptions={optionsList}
          selectedOrgId={selectedOrgId}
          boOptions={boOptions}
        />
      )}

      {showDeleteModal && deletingOption && (
        <OptionsDeleteModal
          modalId={ModalId.AUTH_OPTIONS_DELETE_MODAL}
          title="Delete Auth Option"
          optionName={deletingOption.text}
          optionType="auth"
          onConfirm={onDeleteConfirm}
          onCancel={onDeleteCancel}
        />
      )}

      {showReorderModal && (
        <ReorderModal
          currentSort={sortOption}
          onSave={onReorderSave}
          onClose={onReorderModalClose}
        />
      )}
    </>
  );
};

export default AuthOptionsSection;
